import ButtonsToGenerateReports from '@/components/Reports/ButtonsToGenerateReports';
import FinancialBillsToReceiveForm from '@/components/Reports/FinancialReports/FinancialBillsToReceiveForm';
import { DefaultRouteTabs } from '@/components/_common';
import { QuestResponseType } from '@/core/domain/questReport/Reports/AccountsPayable/AccountsPayable.types';
import { useAccountsReceivableReport } from '@/core/domain/questReport/Reports/AccountsReceivable/AccountsReceivable.hook';
import {
  QuestAccountsReceivableReportType,
  QuestAccountsReceivableRequest,
  REPORT_ACCOUNTS_RECEIVABLE_FILTER_DATE_BY_FORMAT,
  REPORT_ACCOUNTS_RECEIVABLE_ORDER_BY,
  REPORT_ACCOUNTS_RECEIVABLE_REPORT_TYPE,
} from '@/core/domain/questReport/Reports/AccountsReceivable/AccountsReceivable.types';
import useCommonGenerateReport from '@/core/domain/reports/commons/Report.hook';
import {
  FinancialBillsToReceiveFormValues,
  FinancialBillsToReceiveRequestQuery,
  ReportType,
} from '@/core/domain/reports/financial/billsToReceive/billsToReceive.types';
import useFileLoading, { FileType, FILE_FORMAT } from '@/hooks/useFileLoading';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import { PageProps } from '@/types/PageProps';
import { convertParams } from '@/utils/queryParams';
import { Divider, Form } from 'antd';
import { useWatch } from 'antd/lib/form/Form';
import moment from 'moment';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

export default function FinancialBillsToReceivePage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<FinancialBillsToReceiveFormValues>();
  const { setFileLoading, setFileUnloading, fileLoading } = useFileLoading();
  const report = useReportGenerate();

  const isSynthetic = useWatch('synthetic', form);

  const params = convertParams<FinancialBillsToReceiveFormValues>({
    initialDate: 'string',
    finalDate: 'string',
    companiesIds: 'array-number',
    lsSituacao: 'array-string',
    dateType: 'string',
    reportType: 'string',
  });

  useEffect(() => {
    const { initialDate, finalDate, ...rest } = params;

    if (initialDate) form.setFieldValue('initialDate', moment(initialDate));

    if (finalDate) form.setFieldValue('finalDate', moment(finalDate));

    if (rest) {
      form.setFieldsValue(rest);
    }
  }, []);

  const generateReport =
    useCommonGenerateReport<FinancialBillsToReceiveRequestQuery>(
      isSynthetic
        ? '/generate/account-receivable-plan-synthetic'
        : '/generate/accounts-receivable',
      'Relatório gerado com sucesso!',
      'Relatório de contas a receber gerado com sucesso.',
    );

  const questGenerateReport = useAccountsReceivableReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);

    try {
      const { synthetic, ...values } = await form.validateFields();

      const obj: FinancialBillsToReceiveRequestQuery = {
        ...values,
        initialDate: moment(values.initialDate).format('YYYY-MM-DD'),
        finalDate: moment(values.finalDate).format('YYYY-MM-DD'),
        reportType:
          values.reportType === ReportType.chartOfAccounts && synthetic
            ? ReportType.planAccountSynthetic
            : values.reportType,
        format: FILE_FORMAT[format],
      };

      const objQuest: QuestAccountsReceivableRequest = {
        companiesIds: values.companiesIds?.toString(),
        costCenterIds: values.costCenterIds?.toString(),
        bankAccountIds: values.bankAccountIds?.toString(),
        accountsPlanIds: values.planAccountIds?.toString(),
        documentTypeIds: values.documentTypeIds?.toString(),
        insurerIds: values.insurersIds?.toString(),
        customerIds: values.customersIds?.toString(),
        responseType: QuestResponseType.CSV,
        startDate: moment(values.initialDate).format('YYYY-MM-DD'),
        endDate: moment(values.finalDate).format('YYYY-MM-DD'),
        situationOpen: values.lsSituacao.includes('OPEN'),
        situationSettled: values.lsSituacao.includes('SETTLED'),
        filterDateBy:
          REPORT_ACCOUNTS_RECEIVABLE_FILTER_DATE_BY_FORMAT[values.dateType],
        reportType:
          values.reportType === ReportType.chartOfAccounts && synthetic
            ? QuestAccountsReceivableReportType.planAccountSynthetic
            : REPORT_ACCOUNTS_RECEIVABLE_REPORT_TYPE[values.reportType],
        orderBy: REPORT_ACCOUNTS_RECEIVABLE_ORDER_BY[values.reportSortType],
      };

      const result =
        format === 'CSV' && values.reportType === ReportType.chartOfAccounts
          ? await questGenerateReport.mutateAsync(objQuest)
          : await generateReport.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
    } catch {}

    setFileUnloading(format);
  };

  const handleView = () => {
    handleGenerateReport('REPORT_VIEW', (result) =>
      report.openFile({
        result,
        fileName: 'relatorio_contas_a_receber.pdf',
        fileType: 'application/pdf',
      }),
    );
  };

  const handlePdf = () => {
    handleGenerateReport('PDF', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_contas_a_receber.pdf',
      }),
    );
  };

  const handleCsv = () => {
    handleGenerateReport('CSV', (result) =>
      report.downloadFile({
        result,
        fileName: 'relatorio_contas_a_receber.csv',
      }),
    );
  };

  return (
    <DefaultRouteTabs route={route.parent} permissions={route.permissions}>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <FinancialBillsToReceiveForm form={form} />

      <Divider />

      <ButtonsToGenerateReports
        viewReport={handleView}
        generatePdf={handlePdf}
        generateCsv={handleCsv}
        loading={fileLoading}
      />
    </DefaultRouteTabs>
  );
}
